import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useMount } from 'react-use';
import { createFilter } from 'react-select';
import startCase from 'lodash/startCase';
import uniqBy from 'lodash/uniqBy';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useRouteMatch, useHistory, Switch, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Logger } from '../../../infrastructure/logger';
import { CustomError, useAppErrors } from '../../../contexts/app-error-context';
import { reverseSearch } from '../../../services/api';
import { GeocodingFeature } from '../../../types/mapbox-search-api-v1.types.ts';
import Responsive, { useMobile } from '../../responsive/Responsive';
import Icon from '../../icon/Icon';
import {
  photosReducer,
  newPhotosHandler,
  initialLoadingPhotosHandler,
} from '../../controls/photo-upload/PhotoUpload';
import {
  ADD_FEATURE_TYPE,
  compilePath,
  FEEDBACK_WIZARD_STEP,
  namedRoute,
} from '../../../utils/helpers/routes';
import {
  selectFormState,
  formStateChanged,
  clearFormState,
  selectCurrentFormId,
  currentFormIdChanged,
  selectRoutablePoints,
} from '../../../store/item-edit';
import { getLastSubmittedEmail } from '../../../utils/helpers/emailCache';
import {
  EDIT_SUGGESTION_TYPES,
  EDIT_SUGGESTION_VERSION,
} from '../../../utils/constants/constants';
import { selectUserNotificationEnabled } from '../../../store/user-notificaition-settings';
import {
  enhanceEditSuggestoinPayload,
  getReferrer,
  getReferrerAccount,
} from '../../../utils/helpers/editSuggestions';
import { appearances } from '../../controls/button/Button';
import Form, { FormField } from '../form/Form';
import { FIELD_TYPES } from '../field/Field';
import { validators } from '../../../utils/helpers/validators';
import {
  sendEditSuggestionAction,
  storeLastSubmittedEmailAction,
  analyticsTrackAction,
} from '../../../utils/helpers/EditSuggestionActions';
import DefaultFormFooterHint from '../default-form-footer-hint/DefaultFormFooterHint';
import AddRoutablePointsForm, {
  formValidator as routablePointsFormValidator,
} from '../add-routable-points-form/AddRoutablePointsForm';

import './PoiForm.scss';
import { PoiCategory, useAppConfig } from '../../../contexts/app-config';

const addressValidator = validators.required('Address');

const otherPoiCategory: PoiCategory = {
  value: 'other',
  label: 'Other',
};

const getAddressFormFields = ({
  isAdditionForm,
  addressHint,
}: { isAdditionForm?; addressHint? } = {}): FormField[] => [
  {
    field: 'address',
    label: 'Address',
    defaultValue: {
      value: '',
      readonly: false,
    },
    type: FIELD_TYPES.textArea,
    hint: addressHint,
    required: true,
    getValueFromData: (item) => ({
      value: item.properties.description,
      readonly: true,
    }),
    getFormControlValue: (data) => data?.value,
    beforeChange: (value) => ({ value, readonly: false }),
    getValueOnOuterInputChange: async ({
      outerInput,
      getTouched,
      getValue,
    }) => {
      const { itemDragPosition } = outerInput;
      if (!itemDragPosition) return getValue();

      const [lng, lat] = itemDragPosition ? itemDragPosition : [];

      if (!isAdditionForm) {
        return getValue();
      }

      if (getTouched()) {
        return getValue();
      }

      const [reverseGeocodingFeature] = await reverseSearch({
        lng,
        lat,
        types: 'address',
      });

      if (
        getTouched() ||
        !reverseGeocodingFeature ||
        !reverseGeocodingFeature.properties.place_name
      ) {
        return getValue();
      }

      return {
        value: reverseGeocodingFeature.properties.place_name,
        readonly: !isAdditionForm,
      };
    },
    checkChanged: (value, initialValue, outerInput) => {
      return (
        (value.readonly ? false : !isEqual(value, initialValue)) ||
        (value.value && outerInput?.itemMarkerDragged)
      );
    },
    validate: (data) => addressValidator(data?.value),
    controlProps: {
      placeholder: 'Enter the address of business',
    },
  },
];

const getFeedbackFormFields = ({
  categories,
  addressHint,
  isAdditionForm,
  onOpenRoutablePoints,
}: {
  categories;
  addressHint;
  isAdditionForm?;
  onOpenRoutablePoints;
}): FormField[] => {
  const categoriesFilter = createFilter();

  const fields: FormField[] = [
    {
      field: 'name',
      label: 'Name',
      defaultValue: '',
      type: FIELD_TYPES.input,
      required: true,
      getValueFromData: (item) => item.properties.feature_name,
      validate: validators.required('Name'),
      controlProps: {
        placeholder: 'Enter the name of business',
      },
    },
    ...getAddressFormFields({
      isAdditionForm,
      addressHint,
    }),
  ];

  if (!isAdditionForm) {
    fields.push(
      {
        field: 'routable-points-button',
        type: 'button',
        controlProps: {
          children: 'Set Pickup/Arrival',
          appearance: appearances.PRIMARY,
          onClick: onOpenRoutablePoints,
          type: 'button',
        },
      },
      {
        field: 'routable-points',
        type: 'form',
        // should be null for correct initialization of a form
        defaultValue: null,
        validate: validators.validateSelected(
          routablePointsFormValidator,
          (value) => value?.values,
        ),
      },
    );
  }

  fields.push(
    {
      field: 'category',
      label: 'Category',
      defaultValue: '',
      type: FIELD_TYPES.advancedSelect,
      required: true,
      getValueFromData: (item) => item.properties.poi_category![0],
      validate: validators.required('Category'),
      controlProps: {
        placeholder: 'Select the category from the list',
        options: categories,
        filterOption: (candidate, term) => {
          if (candidate.value === otherPoiCategory.value) {
            return true;
          }

          return categoriesFilter(candidate, term);
        },
      },
    },
    {
      field: 'phone',
      label: 'Phone number',
      defaultValue: '',
      type: FIELD_TYPES.input,
      validate: validators.maxLength(20, 'Phone number'),
      controlProps: { placeholder: 'Enter phone number', type: 'tel' },
      getValueFromData: (item) => item.properties.metadata.phone,
    },
    {
      field: 'website',
      label: 'Website',
      defaultValue: '',
      type: FIELD_TYPES.input,
      validate: validators.website,
      controlProps: { placeholder: 'Enter website' },
      getValueFromData: (item) => item.properties.metadata.website,
    },
  );

  return fields;
};

const getPersonalDataFormFields = ({ attachmentsDispatch }): FormField[] => [
  {
    field: 'email',
    label: 'Your email',
    defaultValue: '',
    getPrefillValue: () => getLastSubmittedEmail(),
    required: true,
    type: FIELD_TYPES.input,
    controlProps: {
      placeholder: 'You will get an email when changes are made',
      // name and autocomplete are required for email autocompletion done by browser
      name: 'email',
      autoComplete: 'email',
    },
    validate: validators.compose(
      validators.required('Email'),
      validators.email,
    ),
  },
  {
    field: 'comment',
    label: 'Comments',
    defaultValue: '',
    type: FIELD_TYPES.textArea,
    controlProps: {
      placeholder: 'Add here some additional information',
    },
  },
  {
    field: 'attachments',
    label: 'Photos',
    defaultValue: {},
    type: FIELD_TYPES.photos,
    controlProps: { photosDispatch: attachmentsDispatch },
  },
];

const getJointFormFields = ({
  categories,
  attachmentsDispatch,
  isAdditionForm,
  onOpenRoutablePoints,
}) => [
  ...getFeedbackFormFields({
    isAdditionForm,
    categories,
    addressHint: isAdditionForm
      ? 'Move the location pin to edit the address'
      : 'Move the location pin to edit the location',
    onOpenRoutablePoints,
  }),
  ...getPersonalDataFormFields({ attachmentsDispatch }),
];

const getPoiEditSuggestionPayload = ({
  action,
  reason,
  values,
  mapRoutablePoints,
  initialValues,
  itemDragPosition,
  itemMarkerDragged,
  item,
  userLocation,
  searchQuery,
  searchSelectedFeatureIndex,
  notificationsEnabled,
  datasource,
}: {
  action;
  reason;
  values: Record<string, unknown> & {
    attachments: { fileId }[];
    address: {
      readonly?: boolean;
      value?;
    };
    'routable-points';
  };
  mapRoutablePoints;
  initialValues;
  itemDragPosition;
  itemMarkerDragged;
  item: GeocodingFeature | undefined;
  userLocation;
  searchQuery;
  searchSelectedFeatureIndex;
  notificationsEnabled;
  datasource?: string;
}) => {
  const routablePoints =
    values?.['routable-points']?.values?.['routablePoints'];

  const payload = pickBy(
    {
      version: EDIT_SUGGESTION_VERSION,
      action,
      reason,
      location:
        (item && {
          longitude: item.geometry.coordinates[0],
          latitude: item.geometry.coordinates[1],
        }) ||
        (itemMarkerDragged && {
          longitude: itemDragPosition[0],
          latitude: itemDragPosition[1],
        }),
      userEmail: values.email,
      userLocation: !isEmpty(userLocation)
        ? {
            longitude: userLocation.longitude,
            latitude: userLocation.latitude,
          }
        : null,
      referrer: getReferrer(),
      referrerAccount: getReferrerAccount(),
      notificationsDisabled: !notificationsEnabled,
      searchQuery: searchQuery,
      datasource,
      selectedFeature: item ? JSON.stringify(item) : null,
      selectedFeatureIndex: searchSelectedFeatureIndex,
      mapboxId: item?.properties.mapbox_id,
      changes: pickBy(
        {
          name: values.name === initialValues.name ? null : values.name,
          address: values.address?.readonly ? null : values.address?.value,
          categories:
            values.category === initialValues.category
              ? null
              : [values.category],
          location:
            // there is no sense in sending location change on adding new item
            item && itemMarkerDragged && itemDragPosition
              ? {
                  longitude: itemDragPosition[0],
                  latitude: itemDragPosition[1],
                }
              : null,
          phone: values.phone === initialValues.phone ? null : values.phone,
          website:
            values.website === initialValues.website ? null : values.website,
          routablePoints: routablePoints?.map((routablePoint, idx) =>
            pickBy(
              {
                ...routablePoint,
                location: {
                  longitude: mapRoutablePoints[idx].location[0],
                  latitude: mapRoutablePoints[idx].location[1],
                },
              },
              Boolean,
            ),
          ),
        },
        (val) => !isNil(val) && !isEmpty(val),
      ),
      comment: values.comment === initialValues.comment ? null : values.comment,
      attachments: Object.values(values.attachments)
        .filter(({ fileId }) => fileId)
        .map(({ fileId }) => fileId),
      placeName: item ? item.properties.place_name : null,
    },
    (val) => !isNil(val),
  );

  enhanceEditSuggestoinPayload(payload, item);

  return payload;
};

const getFormId = (isAdditionForm) =>
  isAdditionForm ? 'AddPoiForm' : 'EditPoiForm';

export interface PoiFormProps {
  action;
  item?: GeocodingFeature;
  datasource?: string;
  itemDragPosition;
  itemMarkerDragged;
  searchQuery?;
  searchSelectedFeatureIndex?;
  userLocation;
  onSubmit;
  onCancel;
  reason;
  formDisabled;
}

interface RouteMatchParams {
  wizardStep;
  parameter;
}

const PoiForm = ({
  action,
  item,
  datasource,
  itemDragPosition,
  itemMarkerDragged,
  searchQuery,
  searchSelectedFeatureIndex,
  userLocation,
  onSubmit,
  onCancel,
  reason,
  formDisabled,
}: PoiFormProps) => {
  const { showErrorPage } = useAppErrors();
  const isMobile = useMobile();
  const history = useHistory();
  const match = useRouteMatch<RouteMatchParams>();
  const [isLoading, setIsLoading] = useState(false);

  const isAdditionForm = useMemo(() => action === 'create', [action]);
  const formId = useMemo(() => getFormId(isAdditionForm), [isAdditionForm]);

  const formState = useSelector(selectFormState);
  const currentFormId = useSelector(selectCurrentFormId);
  const notificationsEnabled = useSelector(selectUserNotificationEnabled);

  const { poiCategories } = useAppConfig();

  const formStateRef = useRef<Record<string, unknown>>();
  formStateRef.current = formState;

  const dispatch = useDispatch();
  const setFormState = useCallback(
    (state) => dispatch(formStateChanged(state)),
    [dispatch],
  );

  // clean up other forms to keep local storage size as small as possible
  useMount(() => {
    if (currentFormId !== formId) {
      dispatch(clearFormState());
      dispatch(currentFormIdChanged(formId));
    }
  });

  const [isFeedbackFormShown, setIsFeedbackFormShown] = useState(!!formState);

  // guarantee that feedback is always filled with all the data on mobile
  useEffect(() => {
    if (!isFeedbackFormShown) {
      const firstStep = isAdditionForm
        ? FEEDBACK_WIZARD_STEP.location
        : FEEDBACK_WIZARD_STEP.feedback;

      if (isMobile) {
        if (
          !match.params.wizardStep ||
          match.params.wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation
        ) {
          history.replace({
            pathname: compilePath(
              isAdditionForm ? 'add' : 'edit',
              null,
              isAdditionForm
                ? {
                    featureType: ADD_FEATURE_TYPE.business,
                    wizardStep: firstStep,
                  }
                : {
                    wizardStep: firstStep,
                  },
            ),
            search: history.location.search,
          });
        } else if (match.params.wizardStep === FEEDBACK_WIZARD_STEP.feedback) {
          setIsFeedbackFormShown(true);
        }
      } else {
        setIsFeedbackFormShown(true);
      }
    } else {
      if (isMobile && !match.params.wizardStep) {
        history.replace({
          pathname: compilePath(
            isAdditionForm ? 'add' : 'edit',
            null,
            isAdditionForm
              ? {
                  featureType: ADD_FEATURE_TYPE.business,
                  wizardStep: FEEDBACK_WIZARD_STEP.feedback,
                }
              : {
                  wizardStep: FEEDBACK_WIZARD_STEP.feedback,
                },
          ),
          search: history.location.search,
        });
      }
    }
  }, [isFeedbackFormShown, isMobile, history, isAdditionForm, match]);

  const attachmentsDispatch = useCallback(
    (action) => {
      if (!formStateRef.current) {
        return;
      }

      const newAttachments = photosReducer(
        (formStateRef.current?.values as Record<string, any>).attachments,
        action,
      );
      const newFormState = {
        ...formStateRef.current,
        values: {
          ...(formStateRef.current?.values as Record<string, unknown>),
          attachments: newAttachments,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);

      return action;
    },
    [setFormState],
  );

  const attachments = useMemo(() => formState?.values.attachments, [formState]);

  // reload all still loading images, because it won't be handled anyway
  useMount(() => {
    if (attachments) {
      initialLoadingPhotosHandler(attachments, attachmentsDispatch);
    }
  });

  useEffect(() => {
    if (attachments) {
      newPhotosHandler(attachments, attachmentsDispatch);
    }
  }, [attachments, attachmentsDispatch]);

  const categories = useMemo(() => {
    let categories = poiCategories.slice();
    if (item) {
      categories.push(
        ...item.properties.poi_category!.map((category) => ({
          value: category,
          label: category,
        })),
      );
    }

    categories = uniqBy(categories, (c) => c.value.toLowerCase()).map((c) => ({
      ...c,
      label: c.label
        .split(',')
        .map((l) => startCase(l.trim()))
        .join(', '),
    }));

    categories.sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });

    categories.push(otherPoiCategory);

    return categories;
  }, [item, poiCategories]);

  const handleOpenRoutablePoints = useCallback(
    () =>
      history.push({
        pathname: compilePath('edit', null, {
          wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const jointFormFields = useMemo(
    () =>
      getJointFormFields({
        isAdditionForm,
        categories: categories,
        attachmentsDispatch,
        onOpenRoutablePoints: handleOpenRoutablePoints,
      }),
    [categories, attachmentsDispatch, isAdditionForm, handleOpenRoutablePoints],
  );
  const addressFormFields = useMemo(
    () =>
      getAddressFormFields({
        isAdditionForm,
      }),
    [isAdditionForm],
  );

  const feedbackFormFields = useMemo(
    () =>
      getFeedbackFormFields({
        categories: categories,
        addressHint: (
          <button
            className="poi-form__location-form-toggle-button"
            type="button"
            onClick={() =>
              history.push({
                pathname: compilePath(
                  isAdditionForm ? 'add' : 'edit',
                  null,
                  isAdditionForm
                    ? {
                        featureType: ADD_FEATURE_TYPE.business,
                        wizardStep: FEEDBACK_WIZARD_STEP.location,
                      }
                    : {
                        wizardStep: FEEDBACK_WIZARD_STEP.location,
                      },
                ),
                search: history.location.search,
              })
            }
          >
            <span className="poi-form__location-form-toggle-button-icon">
              <Icon name="pin" />
            </span>
            <span className="poi-form__location-form-toggle-button-text">
              Set location on map →
            </span>
          </button>
        ),
        onOpenRoutablePoints: handleOpenRoutablePoints,
      }),
    [categories, history, isAdditionForm, handleOpenRoutablePoints],
  );

  const personalDataFormFields = useMemo(
    () => getPersonalDataFormFields({ attachmentsDispatch }),
    [attachmentsDispatch],
  );

  const formInitialValues = useMemo(
    () =>
      jointFormFields
        ? jointFormFields.reduce(
            (acc, { field, defaultValue, getValueFromData }) => {
              acc[field] =
                (item && getValueFromData && getValueFromData(item)) ||
                defaultValue;
              return acc;
            },
            {},
          )
        : null,
    [jointFormFields, item],
  );

  useEffect(() => {
    if (!formState && jointFormFields) {
      const values = jointFormFields.reduce(
        (acc, { field, defaultValue, getPrefillValue, getValueFromData }) => {
          acc[field] =
            (item && getValueFromData && getValueFromData(item)) ||
            (getPrefillValue && getPrefillValue()) ||
            defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, item, jointFormFields]);

  const mapRoutablePoints = useSelector(selectRoutablePoints);

  const handleSubmit = useCallback(
    async (values) => {
      const payload = getPoiEditSuggestionPayload({
        action,
        values,
        initialValues: formInitialValues,
        item,
        itemDragPosition,
        itemMarkerDragged,
        searchQuery,
        searchSelectedFeatureIndex,
        userLocation,
        reason,
        notificationsEnabled,
        mapRoutablePoints,
        datasource,
      });

      try {
        setIsLoading(true);

        const editSuggestionResult = await sendEditSuggestionAction(
          {
            editSuggestionType: EDIT_SUGGESTION_TYPES.poi,
            origin: formId,
          },
          payload,
        );

        storeLastSubmittedEmailAction(
          {
            email: values.email,
            origin: formId,
          },
          editSuggestionResult,
        );

        analyticsTrackAction({
          analyticsTrackingMessage: `POI ${
            isAdditionForm ? 'Addition' : 'Edit'
          } Submit`,
          origin: formId,
          analyticsTrackParams: {
            coordinates: itemDragPosition,
          },
        });

        if (onSubmit) {
          onSubmit(editSuggestionResult.result);
        }
      } catch (error) {
        const _error: CustomError = error as CustomError;

        Logger.error('Error while submitting edit suggestion', _error.message);

        showErrorPage({
          type: _error.type,
          message: _error.message,
        } as CustomError);
      } finally {
        setIsLoading(false);
      }
    },
    [
      action,
      isAdditionForm,
      formId,
      formInitialValues,
      item,
      itemDragPosition,
      itemMarkerDragged,
      searchQuery,
      searchSelectedFeatureIndex,
      userLocation,
      onSubmit,
      showErrorPage,
      reason,
      notificationsEnabled,
      mapRoutablePoints,
      datasource,
    ],
  );

  const handleLocationFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath(
          isAdditionForm ? 'add' : 'edit',
          null,
          isAdditionForm
            ? {
                featureType: ADD_FEATURE_TYPE.business,
                wizardStep: FEEDBACK_WIZARD_STEP.feedback,
              }
            : {
                wizardStep: FEEDBACK_WIZARD_STEP.feedback,
              },
        ),
        search: history.location.search,
      }),
    [history, isAdditionForm],
  );

  const handleFeedbackFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath(
          isAdditionForm ? 'add' : 'edit',
          null,
          isAdditionForm
            ? {
                featureType: ADD_FEATURE_TYPE.business,
                wizardStep: FEEDBACK_WIZARD_STEP.additionalInformation,
              }
            : {
                wizardStep: FEEDBACK_WIZARD_STEP.additionalInformation,
              },
        ),
        search: history.location.search,
      }),
    [history, isAdditionForm],
  );

  const handleRoutablePointsFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('edit'),
        search: history.location.search,
      }),
    [history],
  );

  const handleRoutablePointsFormStateChange = useCallback(
    (newRoutablePointsFormState) => {
      if (!formStateRef.current) {
        return;
      }

      const newFormState = {
        ...formStateRef.current,
        values: {
          ...(formStateRef.current?.values as Record<string, unknown>),
          'routable-points': newRoutablePointsFormState,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);
    },
    [setFormState],
  );

  const validateForm = useCallback(
    (values) => {
      if (mapRoutablePoints.some((routablePoint) => !routablePoint.location)) {
        return 'One or several routable points have uninitialized locations.';
      }

      // check that marker has been moved, otherwise there should be changes to the fields
      if (itemMarkerDragged) {
        return false;
      }

      const nonValuableFieldsToOmit = ['email', 'comment', 'attachments'];
      const currentValues = omit(values, nonValuableFieldsToOmit);
      const initialValues = omit(formInitialValues, nonValuableFieldsToOmit);

      if (!currentValues['routable-points']?.values?.routablePoints?.length) {
        // for comparison purposes only, rewrite when there is editing of routable points
        currentValues['routable-points'] = null;
      }

      return isEqual(currentValues, initialValues)
        ? 'Changes to other fields are required to submit the feedback'
        : null;
    },
    [formInitialValues, itemMarkerDragged, mapRoutablePoints],
  );

  const { wizardStep } = match.params;
  const outerInput = useMemo(
    () => ({ itemDragPosition, itemMarkerDragged }),
    [itemDragPosition, itemMarkerDragged],
  );

  if (!formState || !formInitialValues) {
    return null;
  }

  return (
    <Switch>
      <Route
        path={namedRoute(
          'routable-points',
          compilePath('edit', null, {
            wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
          }),
        )}
      >
        <AddRoutablePointsForm
          baseUrl={compilePath('edit', null, {
            wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
          })}
          item={item}
          poiCategories={formState ? [formState.values['category']] : []}
          formState={formState?.values['routable-points']}
          onFormStateChange={handleRoutablePointsFormStateChange}
          onSubmit={handleRoutablePointsFormSubmit}
        />
      </Route>
      <Route>
        <Responsive mobile={false}>
          <Form
            fields={jointFormFields}
            state={formState}
            initialValues={formInitialValues}
            outerInput={outerInput}
            formDisabled={formDisabled || isLoading}
            validate={validateForm}
            onStateChange={setFormState}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            footerHint={<DefaultFormFooterHint />}
          />
        </Responsive>
        <Responsive mobile={true}>
          {wizardStep === FEEDBACK_WIZARD_STEP.location && (
            <Form
              state={formState}
              initialValues={formInitialValues}
              fields={addressFormFields}
              outerInput={outerInput}
              submitLabel="Next"
              formDisabled={formDisabled}
              onStateChange={setFormState}
              onSubmit={handleLocationFormSubmit}
            />
          )}
          {wizardStep === FEEDBACK_WIZARD_STEP.feedback && (
            <Form
              state={formState}
              initialValues={formInitialValues}
              fields={feedbackFormFields}
              outerInput={outerInput}
              submitLabel="Next"
              validate={validateForm}
              formDisabled={formDisabled}
              stepLabel="1/2"
              onStateChange={setFormState}
              onSubmit={handleFeedbackFormSubmit}
            />
          )}

          {wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation && (
            <Form
              state={formState || { values: formInitialValues }}
              initialValues={formInitialValues}
              fields={personalDataFormFields}
              validate={validateForm}
              formDisabled={isLoading}
              stepLabel="2/2"
              onStateChange={setFormState}
              onSubmit={handleSubmit}
              footerHint={<DefaultFormFooterHint />}
            />
          )}
        </Responsive>
      </Route>
    </Switch>
  );
};

export default PoiForm;
